import { useEffect } from "react";

const GLASS_SURFACE_SELECTOR =
  ".glass-panel, .glass-panel-strong, .glass-pill";

/** Seconds — higher = slower, blobbier follow (not 1:1 with cursor). */
const SMOOTH_TAU_S = 0.22;
const SETTLE_EPS = 0.06;
/** Matches `--glass-crossfade-duration` in globals.css. */
const CROSSFADE_MS = 520;

/** True when this element should receive the cursor-following backdrop glow. */
function isGlowEligible(el: HTMLElement): boolean {
  if (el.classList.contains("glass-no-hover")) return false;
  if (el.classList.contains("shell-chrome")) return false;
  if (el.classList.contains("glass-overlay")) return false;
  const shell = el.closest(".shell-chrome");
  if (shell && shell !== el) return false;
  return (
    el.classList.contains("glass-panel") ||
    el.classList.contains("glass-panel-strong") ||
    el.classList.contains("glass-pill")
  );
}

/** Topmost eligible glass surface under the pointer (handles nested children). */
function findGlassSurfaceAt(
  clientX: number,
  clientY: number,
): HTMLElement | null {
  const stack = document.elementsFromPoint(clientX, clientY);
  for (const el of stack) {
    if (!(el instanceof HTMLElement)) continue;
    const surface = el.closest(GLASS_SURFACE_SELECTOR);
    if (surface instanceof HTMLElement && isGlowEligible(surface)) {
      return surface;
    }
  }
  return null;
}

function pointerToPercent(
  surface: HTMLElement,
  clientX: number,
  clientY: number,
): { x: number; y: number } | null {
  const rect = surface.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    x: ((clientX - rect.left) / rect.width) * 100,
    y: ((clientY - rect.top) / rect.height) * 100,
  };
}

function applyGlowPosition(
  surface: HTMLElement,
  x: number,
  y: number,
): void {
  surface.style.setProperty("--glass-glow-x", `${x.toFixed(2)}%`);
  surface.style.setProperty("--glass-glow-y", `${y.toFixed(2)}%`);
}

function clearGlowStyles(surface: HTMLElement): void {
  surface.style.removeProperty("--glass-glow-x");
  surface.style.removeProperty("--glass-glow-y");
}

interface GlowState {
  surface: HTMLElement;
  currentX: number;
  currentY: number;
  targetX: number;
  targetY: number;
}

/**
 * Tracks pointer position over glass surfaces and drives `--glass-glow-x/y` plus
 * glow classes with eased motion and cross-card crossfade.
 */
export function useGlassHoverGlow(): void {
  useEffect(() => {
    if (!window.matchMedia("(hover: hover)").matches) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const crossfadeMs = reducedMotion ? 120 : CROSSFADE_MS;

    let state: GlowState | null = null;
    let rafId = 0;
    let lastFrameTime = 0;
    let pointerX = 0;
    let pointerY = 0;
    let tracking = false;
    const exitTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

    function cancelExit(surface: HTMLElement): void {
      const timer = exitTimers.get(surface);
      if (timer !== undefined) {
        clearTimeout(timer);
        exitTimers.delete(surface);
      }
      surface.classList.remove("glass-glow-exiting");
    }

    function finishExit(surface: HTMLElement): void {
      cancelExit(surface);
      surface.classList.remove(
        "glass-glow-active",
        "glass-glow-entering",
        "glass-glow-exiting",
      );
      clearGlowStyles(surface);
    }

    /** Fade haze out in place — keeps last blob position frozen on the card. */
    function startExit(surface: HTMLElement): void {
      if (
        !surface.classList.contains("glass-glow-active") &&
        !surface.classList.contains("glass-glow-exiting")
      ) {
        return;
      }

      cancelExit(surface);
      surface.classList.add("glass-glow-exiting");
      // One frame at full strength, then drop active so opacity transitions out.
      void surface.offsetWidth;
      surface.classList.remove("glass-glow-active", "glass-glow-entering");

      exitTimers.set(
        surface,
        setTimeout(() => finishExit(surface), crossfadeMs),
      );
    }

    function fadeIn(surface: HTMLElement): void {
      if (reducedMotion) return;
      surface.classList.add("glass-glow-entering");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          surface.classList.remove("glass-glow-entering");
        });
      });
    }

    function createGlowState(
      surface: HTMLElement,
      target: { x: number; y: number },
      mode: "first-entry" | "crossfade" | "instant",
    ): GlowState {
      cancelExit(surface);
      surface.classList.add("glass-glow-active");
      surface.classList.remove("glass-glow-exiting");

      if (mode === "first-entry" && !reducedMotion) {
        fadeIn(surface);
        const glow: GlowState = {
          surface,
          currentX: target.x + (50 - target.x) * 0.28,
          currentY: target.y + (50 - target.y) * 0.28,
          targetX: target.x,
          targetY: target.y,
        };
        applyGlowPosition(surface, glow.currentX, glow.currentY);
        return glow;
      }

      if (mode === "crossfade" && !reducedMotion) {
        fadeIn(surface);
      }

      const glow: GlowState = {
        surface,
        currentX: target.x,
        currentY: target.y,
        targetX: target.x,
        targetY: target.y,
      };
      applyGlowPosition(surface, target.x, target.y);
      return glow;
    }

    function switchSurface(
      from: HTMLElement,
      to: HTMLElement,
      target: { x: number; y: number },
    ): void {
      startExit(from);
      state = createGlowState(to, target, "crossfade");
    }

    function leaveAll(): void {
      if (state) {
        startExit(state.surface);
        state = null;
      }
      tracking = false;
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      lastFrameTime = 0;
    }

    function tick(now: number): void {
      rafId = 0;

      if (!tracking || !state) return;

      const surface = findGlassSurfaceAt(pointerX, pointerY);
      if (!surface) {
        leaveAll();
        return;
      }

      const target = pointerToPercent(surface, pointerX, pointerY);
      if (!target) return;

      if (state.surface !== surface) {
        switchSurface(state.surface, surface, target);
      } else {
        state.targetX = target.x;
        state.targetY = target.y;
      }

      if (reducedMotion) {
        applyGlowPosition(state.surface, state.targetX, state.targetY);
        return;
      }

      const dt = Math.min((now - lastFrameTime) / 1000, 0.05);
      lastFrameTime = now;
      const lerp = 1 - Math.exp(-dt / SMOOTH_TAU_S);

      state.currentX += (state.targetX - state.currentX) * lerp;
      state.currentY += (state.targetY - state.currentY) * lerp;
      applyGlowPosition(state.surface, state.currentX, state.currentY);

      const dx = state.targetX - state.currentX;
      const dy = state.targetY - state.currentY;
      if (dx * dx + dy * dy >= SETTLE_EPS * SETTLE_EPS) {
        scheduleTick();
      }
    }

    function scheduleTick(): void {
      if (rafId !== 0) return;
      rafId = requestAnimationFrame((now) => {
        if (lastFrameTime === 0) lastFrameTime = now;
        tick(now);
      });
    }

    function onPointerMove(e: PointerEvent): void {
      if (e.pointerType === "touch") return;

      pointerX = e.clientX;
      pointerY = e.clientY;

      const surface = findGlassSurfaceAt(pointerX, pointerY);
      if (!surface) {
        if (tracking) leaveAll();
        return;
      }

      const target = pointerToPercent(surface, pointerX, pointerY);
      if (!target) return;

      tracking = true;

      if (!state) {
        state = createGlowState(surface, target, "first-entry");
        lastFrameTime = 0;
      } else if (state.surface !== surface) {
        switchSurface(state.surface, surface, target);
        lastFrameTime = 0;
      }

      scheduleTick();
    }

    function onPointerLeave(e: PointerEvent): void {
      if (e.pointerType === "touch") return;
      if (e.relatedTarget !== null) return;
      leaveAll();
    }

    /** Glow layers are pinned to the glass scrollport — dismiss when inner content scrolls. */
    function onScroll(e: Event): void {
      if (!state) return;
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (target === state.surface || state.surface.contains(target)) {
        leaveAll();
      }
    }

    document.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("pointerleave", onPointerLeave);
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });

    return () => {
      leaveAll();
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerleave", onPointerLeave);
      document.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, []);
}
